import { useTranslation } from 'react-i18next'
import { useCogfy, useToasts } from '../../../hooks'
import { Button, ButtonProps, Dialog, Input } from '../../../components'
import { useState } from 'react'
import { PlusIcon } from 'lucide-react'
import { useFormik } from 'formik'
import * as yup from 'yup'

type FormValues = {
  name: string
  host: string
  port: string
  database: string
  user: string
  password: string
  caCertificate: File | null
}

export type AddPostgresqlButtonProps = {
  onSuccess?: () => void
}

export function AddPostgresqlButton (props: AddPostgresqlButtonProps & ButtonProps) {
  const { onSuccess } = props

  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const cogfy = useCogfy()
  const toasts = useToasts()
  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      host: '',
      port: '',
      database: '',
      user: '',
      password: '',
      caCertificate: null
    },
    validationSchema: yup.object().shape({
      name: yup.string().required(t('Required field')),
      host: yup.string().required(t('Required field')),
      port: yup.number().required(t('Required field')),
      database: yup.string().required(t('Required field')),
      user: yup.string().required(t('Required field')),
      password: yup.string().required(t('Required field')),
      caCertificate: yup.mixed().required(t('Required field'))
    }),
    onSubmit: async values => {
      try {
        const caCertificateContent = await values.caCertificate?.arrayBuffer()
        const caCertificateString = new TextDecoder().decode(caCertificateContent)

        await cogfy.createConnection({
          provider: 'postgresql',
          name: values.name,
          values: [
            { name: 'host', value: values.host },
            { name: 'port', value: String(values.port) },
            { name: 'database', value: values.database },
            { name: 'user', value: values.user },
            { name: 'password', value: values.password },
            { name: 'caCertificate', value: caCertificateString }
          ]
        })
        onSuccess?.()
        resetFormAndClose()
      } catch (error) {
        toasts.error(t('Unexpected error'))
      }
    }
  })

  const resetFormAndClose = () => {
    const fileInput = document.getElementById('add_postgresql_button_certificate_file_input') as HTMLInputElement

    if (fileInput) {
      fileInput.value = ''
    }

    formik.resetForm()
    setOpen(false)
  }

  return (
    <>
      <Button
        color={props.color}
        size={props.size || 'sm'}
        onClick={() => setOpen(true)}
        className={props.className}
      >
        <PlusIcon size={16} />
        {props.children}
      </Button>

      <Dialog
        open={open}
        onClose={() => resetFormAndClose()}
      >
        <h3 className="font-bold text-lg">
          {t('WorkspaceConnectionsPage:Configure PostgreSQL database')}
        </h3>
        <form
          className="flex flex-col gap-4 my-4"
          onSubmit={formik.handleSubmit}
        >
          <Input
            label={t('WorkspaceConnectionsPage:Connection name')}
            error={Boolean(formik.touched.name && formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            {...formik.getFieldProps('name')}
          />

          <Input
            label="Host"
            error={Boolean(formik.touched.host && formik.errors.host)}
            helperText={formik.touched.host && formik.errors.host}
            {...formik.getFieldProps('host')}
          />

          <Input
            label={t('WorkspaceConnectionsPage:Port')}
            type="number"
            error={Boolean(formik.touched.port && formik.errors.port)}
            helperText={formik.touched.port && formik.errors.port}
            {...formik.getFieldProps('port')}
          />

          <Input
            label={t('WorkspaceConnectionsPage:Database')}
            error={Boolean(formik.touched.database && formik.errors.database)}
            helperText={formik.touched.database && formik.errors.database}
            {...formik.getFieldProps('database')}
          />

          <Input
            label={t('WorkspaceConnectionsPage:User')}
            error={Boolean(formik.touched.user && formik.errors.user)}
            helperText={formik.touched.user && formik.errors.user}
            {...formik.getFieldProps('user')}
          />

          <Input
            label={t('WorkspaceConnectionsPage:Password')}
            type="password"
            error={Boolean(formik.touched.password && formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            {...formik.getFieldProps('password')}
          />

          <Input
            id="add_postgresql_button_certificate_file_input"
            label={t('WorkspaceConnectionsPage:CA certificate')}
            type="file"
            className="file-input file-input-sm w-full"
            error={Boolean(formik.touched.caCertificate && formik.errors.caCertificate)}
            helperText={formik.touched.caCertificate && formik.errors.caCertificate}
            onChange={(event) => {
              const file = event.currentTarget.files?.[0]
              formik.setFieldValue('caCertificate', file)
            }}
          />

          <div className="modal-action">
            <Button
              size="sm"
              type="button"
              disabled={formik.isSubmitting}
              onClick={() => resetFormAndClose()}
            >
              {t('Close')}
            </Button>
            <Button
              color="primary"
              size="sm"
              type="submit"
              disabled={formik.isSubmitting}
            >
              {t('Confirm')}
            </Button>
          </div>
        </form>
      </Dialog>
    </>
  )
}
